// "POSSIBLE ONGOING MATCHES" — the step between "Add to ongoing" and the picker (2026-09-15 parity
// audit F1-4, bff#40; frames 40010646:340618 → 40010646:341095 → 40010904:366285 → 40010903:365824
// → 40010912:367754).
//
// Choosing "Add to ongoing" used to swap the middle for the whole "Ongoing Workspace" listing at
// once. The frames put a step in front of that: the radio ARMS an "Add" CTA; pressing Add opens a
// "‹ Possible Ongoing matches" panel in the box with the three best EXISTING Ongoings as radios
// (the best labelled "Strong match"), the input reading "Search for an Ongoing to add to…", and the
// CTA reading "Search" once something is typed — three dots while it looks, then the page-level
// listing filtered by the query. Picking a radio and pressing Add commits with the receipt the
// picker already writes.
//
// The ranking is the BFF's (`POST v1/ongoing/suggest-from-threads` → `existing[]`, and
// `GET v1/ongoing/search?q=` for the search step). This module is the panel's pure half: the
// request bodies, the rows, and what the CTA says in each state.
//
// ── SUPERSEDED IN PART BY busy-my-web#523 / #526 (2026-09-17) ───────────────────────────────────
// The PANEL is gone: Fatemeh's redesign takes the ranked Ongoings out of the box entirely and puts
// them at the top of the middle's listing (components/Email/OngoingPickerList.vue,
// lib/ongoingPicker's `possibleMatchListRows`). What survives here is the half that was never about
// the panel's chrome — the request body, the reading of `existing[]`, and the rows — because the
// ranking itself is unchanged and still orders that listing. `PANEL_TEXT.placeholder` is still the
// box's prompt while the listing is up.
//
// `defaultMatchRows` is no longer called: standing the user's own Ongoings in as rows when nothing
// ranked is exactly what put the fish and the bread on Saffet's screen (#526). They are still shown
// — in the listing under the matches, unmarked — which is what he asked for instead.
//
// Pure — no store, no api, nothing mounted.

export const PANEL_TEXT = {
  title: 'Possible Ongoing matches',
  placeholder: 'Search for an Ongoing to add to...',
  add: 'Add',
  search: 'Search',
  strong: 'Strong match',
  likely: 'Likely match',
  possible: 'Possible match',
  none: 'No obvious match yet — search for an Ongoing below.',
  loading: 'Looking for matches…',
}

/** How many matches the panel offers (the frames draw three). */
export const PANEL_TAKE = 3

/** The request body for `POST v1/ongoing/suggest-from-threads` ranking existing Ongoings. */
export function matchesRequest({ timelineIds = [], senders = [], take = PANEL_TAKE } = {}) {
  const body = { take }
  const ids = (timelineIds || []).filter(Boolean).map(String)
  const from = (senders || []).filter(Boolean).map(String)
  if (ids.length) body.timelineIds = ids
  if (from.length) body.senders = from
  return body
}

/** The `existing[]` rows an answer carries — from either endpoint — junk dropped, order kept. */
export function readExisting(payload) {
  const body = payload?.data?.data ?? payload?.data ?? payload ?? {}
  return (Array.isArray(body?.existing) ? body.existing : [])
    .filter(r => r && r.ongoingId)
    .map(r => ({
      id: String(r.ongoingId),
      title: String(r.title || '').trim() || 'Ongoing',
      confidence: typeof r.confidence === 'number' ? r.confidence : null,
      label: String(r.label || '').trim(),
      strong: !!r.strong,
      reasons: (r.reasons || []).filter(Boolean).map(String),
    }))
}

/**
 * The panel's rows: the top three, the best one wearing its label when it is a strong match
 * (frame 40010646:341095 labels only "Recruitment / New role  Strong match").
 */
// …and the ranking's own evidence rides along (busy-my-web#535): the matches TABLE that replaced the
// panel (Figma 40011882:372321) draws WHY a story may belong ("Possible connection", the first of
// `reasons`) and how sure the ranking is (the Strong / Likely / Possible chip, from `label`).
export function matchRows(existing, take = PANEL_TAKE) {
  return (existing || []).slice(0, take).map(r => ({
    id: r.id,
    title: r.title,
    badge: r.strong ? (r.label || PANEL_TEXT.strong) : '',
    label: r.label || '',
    strong: !!r.strong,
    confidence: typeof r.confidence === 'number' ? r.confidence : null,
    reasons: Array.isArray(r.reasons) ? r.reasons : [],
  }))
}

/**
 * The DEFAULT rows when the ranking returns nothing (Pouria, 2026-09-16: "we need to show some
 * default name matches"): the user's own top-level Ongoings, newest first as the store keeps them,
 * capped at the panel's three, with no badge — nothing was matched, these are simply where an
 * email can go. The search below still finds the rest.
 */
export function defaultMatchRows(ongoings, take = PANEL_TAKE) {
  return (ongoings || [])
    .filter(o => o && o.id && !o.parentOngoingId)
    .slice(0, take)
    .map(o => ({ id: String(o.id), title: String(o.title || '').trim() || 'Ongoing', badge: '' }))
}

/**
 * What the action bar's CTA says while the panel is up (frames 40010646:341095 → 40010912:367754):
 *   · a match picked            → "Add", live
 *   · something typed           → "Search", live (the three dots while it runs)
 *   · nothing picked, nothing typed → "Add", disabled
 */
export function panelCta({ picked = '', query = '', searching = false, running = false } = {}) {
  const q = String(query || '').trim()
  if (q) return { label: PANEL_TEXT.search, disabled: !!searching || !!running, searching: !!searching }
  return { label: PANEL_TEXT.add, disabled: !picked || !!running, searching: false }
}

export default matchRows
